import { LazyMotion, domAnimation, m } from "framer-motion";
import type { RefObject } from "react";
import { useEffect, useRef } from "react";
import MessageBubble from "./MessageBubble";
import TransferNotice from "./TransferNotice";
import type { Conversation, InboxDemoIcons, Message } from "../data/types";
import {
  messageEnterAnimate,
  messageEnterInitial,
  messageEnterTransition,
} from "../hooks/messageMotion";

type MessageListProps = {
  conversation?: Conversation;
  messages: Message[];
  listRef: RefObject<HTMLDivElement | null>;
  icons: InboxDemoIcons;
  operatorName?: string;
  operatorAvatar?: string;
  onUserIntent: () => void;
};

export default function MessageList({
  conversation,
  messages,
  listRef,
  icons,
  operatorName,
  operatorAvatar,
  onUserIntent,
}: MessageListProps) {
  const skipScrollIntentRef = useRef(false);

  useEffect(() => {
    const node = listRef.current;
    if (!node) return;
    skipScrollIntentRef.current = true;
    node.scrollTo({
      top: node.scrollHeight,
      behavior: "smooth",
    });
    const id = window.setTimeout(() => {
      skipScrollIntentRef.current = false;
    }, 480);
    return () => window.clearTimeout(id);
  }, [listRef, messages]);

  return (
    <LazyMotion features={domAnimation}>
      <div
        className="inbox-demo-scroll flex min-h-0 flex-1 flex-col gap-5 px-3 pb-6 pt-5 md:px-6"
        ref={listRef}
        onScroll={() => {
          if (skipScrollIntentRef.current) return;
          onUserIntent();
        }}
        onWheel={onUserIntent}
      >
        {messages.map((message, index) => {
          const isHandoff =
            message.sender === "human" && messages[index - 1]?.sender !== "human";

          return (
            <div className="flex w-full flex-col gap-5" key={message.id}>
              {message.dateLabel ? (
                <m.p
                  animate={{ opacity: 1 }}
                  className="w-full text-center text-xs leading-4 text-default-500"
                  initial={{ opacity: 0 }}
                  transition={messageEnterTransition}
                >
                  {message.dateLabel}
                </m.p>
              ) : null}
              {isHandoff ? (
                <m.div
                  animate={messageEnterAnimate}
                  initial={messageEnterInitial}
                  transition={messageEnterTransition}
                >
                  <TransferNotice
                    icons={icons}
                    operatorAvatar={operatorAvatar}
                    operatorName={operatorName || message.senderName || "客服"}
                    time={message.timestamp}
                  />
                </m.div>
              ) : null}
              <MessageBubble
                conversation={conversation}
                message={message}
                operatorAvatar={operatorAvatar}
                operatorName={operatorName}
              />
            </div>
          );
        })}
      </div>
    </LazyMotion>
  );
}
